import { useState, useEffect } from 'react';

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import {
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  type BillingPeriod,
  getPlanPrice,
  getPlanPriceId,
  getPracticePeriodText,
  PLANS,
} from '~/config/plans';
import { usePostHog, EVENTS } from '~/hooks/usePostHog';
import type { AuthLoaderData } from '~/routes/_layout';

let stripePromise: ReturnType<typeof loadStripe> | null = null;

// Utility function to log only on client side
const logOnClient = (level: 'info' | 'error', message: string, data?: any) => {
  if (typeof window !== 'undefined') {
    console[level](`[SubscriptionModal] ${message}`, data);
  }
};

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: keyof typeof PLANS;
  context: AuthLoaderData;
  isCancelled?: boolean;
  renewalDate?: string;
}

type ModalView = 'plans' | 'payment' | 'success' | 'loading';

interface SuccessViewProps {
  plan: keyof typeof PLANS;
  onClose: () => void;
}

interface PaymentFormProps {
  clientSecret: string;
  plan: keyof typeof PLANS;
  onSuccess: (plan: keyof typeof PLANS) => void;
  onCancel: () => void;
}

function PaymentForm({
  clientSecret,
  plan,
  onSuccess,
  onCancel,
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { _ } = useLingui();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Stripe is not initialized');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-confirmation`,
        },
        redirect: 'if_required',
      });

      if (error) {
        logOnClient('error', 'Payment failed', {
          metadata: { error },
        });
        setErrorMessage(error.message || 'Payment failed');
        return;
      }

      if (
        paymentIntent.status === 'succeeded' ||
        paymentIntent.status === 'processing'
      ) {
        logOnClient('info', 'Payment successful');
        toast.success(_(msg`Payment successful!`));
        onSuccess(plan);
      } else {
        setErrorMessage(`Payment failed with status: ${paymentIntent.status}`);
      }
    } catch (error) {
      logOnClient('error', 'Subscription error', error);
      setErrorMessage('An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <PaymentElement />
        {errorMessage && (
          <div className="text-sm text-red-600 bg-red-50 p-4 rounded-lg border border-red-200 font-medium">
            {errorMessage}
          </div>
        )}
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isProcessing}
          className="w-full sm:w-auto py-6 text-base font-semibold rounded-xl border-2 border-brand-dark-blue/20 hover:bg-brand-dark-blue/5"
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full sm:w-auto min-w-[180px] py-6 text-base font-bold rounded-xl bg-gradient-to-r from-brand-dark-blue to-brand-green hover:from-brand-dark-blue/90 hover:to-brand-green/90 shadow-lg"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              <Trans>Processing...</Trans>
            </>
          ) : (
            <Trans>Pay ${getPlanPrice(plan, billingPeriod)}</Trans>
          )}
        </Button>
      </div>
    </form>
  );
}

function SuccessView({ plan, onClose }: SuccessViewProps) {
  const isCancellation = plan === 'free';

  const handleClose = () => {
    // If we're on the processing route and subscription was successful,
    // trigger processing to continue
    if (!isCancellation && window.location.pathname === '/processing') {
      window.dispatchEvent(new CustomEvent('startProcessing'));
    }
    onClose();
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 sm:px-8 text-center">
      {isCancellation ? (
        <>
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-brand-gold/20 rounded-full blur-2xl animate-pulse"></div>
            <AlertCircle className="relative h-20 w-20 text-brand-gold" />
          </div>
          <h2 className="text-3xl font-bold mb-4 text-brand-dark-blue"><Trans>Subscription Cancelled</Trans></h2>
          <p className="text-brand-dark-blue/70 mb-8 max-w-md text-base leading-relaxed">
            <Trans>Your subscription has been cancelled. You will still have access to premium features until the end of your current billing period. After that, you will be moved to the free plan with</Trans> {getPracticePeriodText('free')}.
          </p>
        </>
      ) : (
        <>
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-brand-green/20 rounded-full blur-2xl animate-pulse"></div>
            <CheckCircle2 className="relative h-20 w-20 text-brand-green" />
          </div>
          <h2 className="text-3xl font-bold mb-4 text-brand-dark-blue"><Trans>Subscription Successful!</Trans></h2>
          <p className="text-brand-dark-blue/70 mb-8 max-w-md text-base leading-relaxed">
            <Trans>Thank you for subscribing to the</Trans> <span className="font-semibold text-brand-dark-blue">{PLANS[plan].name}</span> <Trans>plan. You now have</Trans> {getPracticePeriodText(plan)}.
          </p>
        </>
      )}
      <Button
        onClick={handleClose}
        className="w-full max-w-sm py-6 text-base font-bold rounded-xl bg-gradient-to-r from-brand-dark-blue to-brand-green hover:from-brand-dark-blue/90 hover:to-brand-green/90 shadow-lg"
      >
        {isCancellation ? <Trans>Close</Trans> : <Trans>Start Practicing</Trans>}
      </Button>
    </div>
  );
}

function LoadingView() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 sm:px-8 text-center">
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-brand-dark-blue/20 rounded-full blur-2xl animate-pulse"></div>
        <Loader2 className="relative h-16 w-16 animate-spin text-brand-dark-blue" />
      </div>
      <h2 className="text-2xl font-bold mb-3 text-brand-dark-blue">
        <Trans>Setting up your payment</Trans>
      </h2>
      <p className="text-brand-dark-blue/70 text-base">
        <Trans>Redirecting to secure checkout...</Trans>
      </p>
    </div>
  );
}

export default function SubscriptionModal({
  isOpen,
  onClose,
  currentPlan,
  context,
  isCancelled = false,
  renewalDate,
}: SubscriptionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<keyof typeof PLANS | null>(
    null
  );
  const [view, setView] = useState<ModalView>('plans');
  const [successPlan, setSuccessPlan] = useState<keyof typeof PLANS | null>(
    null
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<keyof typeof PLANS | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');
  const { stripePublicKey } = context;
  const { track } = usePostHog();
  const { _ } = useLingui();

  // Track modal opened with PostHog and Meta Pixel
  useEffect(() => {
    if (isOpen) {
      // PostHog tracking
      track(EVENTS.SUBSCRIPTION_MODAL_OPENED, {
        current_plan: currentPlan,
        is_cancelled: isCancelled,
        renewal_date: renewalDate
      });
      
      // Meta Pixel tracking - ViewContent for subscription options
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'ViewContent', {
          content_name: 'Subscription Plans',
          content_category: 'Pricing',
          current_plan: currentPlan,
        });
      }
    }
  }, [isOpen, currentPlan, isCancelled, renewalDate, track]);

  // Initialize Stripe only once when the key is available
  if (stripePublicKey && !stripePromise) {
    stripePromise = loadStripe(stripePublicKey);
  }

  const handleClose = () => {
    onClose();
    // Reset the view after the modal closes
    setTimeout(() => {
      setView('plans');
      setSuccessPlan(null);
      setClientSecret(null);
      setSelectedPlan(null);
      setError(null);
    }, 300); // Match this with your modal's close animation duration
  };

  const handleSubscribe = async (plan: keyof typeof PLANS) => {
    setIsLoading(true);
    setLoadingPlan(plan);
    setError(null);
    setView('loading'); // Set loading view immediately

    // Track InitiateCheckout with Meta Pixel
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: `${PLANS[plan].name} Plan`,
        content_category: 'Subscription',
        value: getPlanPrice(plan, billingPeriod),
        currency: 'USD',
        billing_period: billingPeriod,
      });
    }

    try {
      // Include the current pathname so we can redirect back after subscription
      const currentPath = window.location.pathname;
      const returnUrl =
        currentPath === '/processing' ? '/processing' : '/practice';

      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan, billingPeriod, returnUrl }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create subscription');
      }

      if (!data.sessionUrl) {
        throw new Error('No checkout session URL returned');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.sessionUrl;
    } catch (error) {
      logOnClient('error', 'Subscription error', {
        metadata: { error },
      });
      setError(error instanceof Error ? error.message : 'An error occurred');
      toast.error(error instanceof Error ? error.message : _(msg`An error occurred`));
      setView('plans'); // Return to plans view on error
    } finally {
      setIsLoading(false);
      setLoadingPlan(null);
    }
  };

  const handlePaymentSuccess = (plan: keyof typeof PLANS) => {
    // Track Purchase with Meta Pixel
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Purchase', {
        content_name: `${PLANS[plan].name} Plan`,
        content_type: 'product',
        value: getPlanPrice(plan, billingPeriod),
        currency: 'USD',
        billing_period: billingPeriod,
      });
    }
    
    setSuccessPlan(plan);
    setView('success');
  };

  logOnClient('info', 'SubscriptionModal render', {
    metadata: {
      isOpen,
      view,
      currentPlan,
      stripePublicKey: !!stripePublicKey,
      modalProps: { isOpen, currentPlan, isCancelled, renewalDate },
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0">
        {view === 'plans' ? (
          <div className="p-6 sm:p-8">
            <DialogHeader className="space-y-4 mb-6">
              <DialogTitle className="text-2xl sm:text-3xl font-bold text-brand-dark-blue text-center">
                <Trans>Choose Your Plan</Trans>
              </DialogTitle>
              <DialogDescription className="text-center space-y-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mt-4">
                  <span
                    className={
                      billingPeriod === 'monthly'
                        ? 'font-bold text-brand-dark-blue text-base'
                        : 'text-brand-dark-blue/70 text-base'
                    }
                  >
                    <Trans>Billed monthly</Trans>
                  </span>
                  <button
                    type="button"
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-dark-blue focus:ring-offset-2 ${billingPeriod === 'annually' ? 'bg-brand-dark-blue' : 'bg-gray-300'}`}
                    onClick={() =>
                      setBillingPeriod(
                        billingPeriod === 'monthly' ? 'annually' : 'monthly'
                      )
                    }
                    aria-pressed={billingPeriod === 'annually'}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${billingPeriod === 'annually' ? 'translate-x-7' : 'translate-x-1'}`}
                    />
                  </button>
                  <span
                    className={
                      billingPeriod === 'annually'
                        ? 'font-bold text-brand-dark-blue text-base'
                        : 'text-brand-dark-blue/70 text-base'
                    }
                  >
                    <Trans>Billed annually</Trans>
                  </span>
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-bold ${billingPeriod === 'annually' ? 'bg-brand-gold text-white' : 'bg-gray-200 text-gray-600'}`}
                  >
                    <Trans>SAVE 40%</Trans>
                  </span>
                </div>
                <p className="text-brand-dark-blue/70 text-base">
                  <Trans>Select the plan that best fits your needs</Trans>
                </p>
                {isCancelled && currentPlan !== 'free' && (
                  <div className="bg-brand-gold/10 border border-brand-gold/30 text-brand-gold flex items-center justify-center gap-2 p-3 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium"><Trans>Your subscription will be cancelled on</Trans> {renewalDate}</span>
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 flex items-center justify-center gap-2 p-3 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">{error}</span>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 grid-cols-1 md:grid-cols-3 mt-8">
              {(['free', 'pro', 'advanced'] as (keyof typeof PLANS)[]).map(
                key => {
                  const plan = PLANS[key];
                  const isPlanLoading = loadingPlan === key;
                  const isCurrentPlan = currentPlan === key;
                  const price = getPlanPrice(key, billingPeriod);
                  const isAdvanced = key === 'advanced';
                  return (
                    <div
                      key={key}
                      className={`relative rounded-2xl border-2 p-6 transition-all duration-200 hover:scale-105 hover:shadow-2xl flex flex-col ${
                        isAdvanced
                          ? 'border-brand-gold bg-gradient-to-br from-brand-gold/5 to-brand-green/5 shadow-lg'
                          : isCurrentPlan
                          ? isCancelled
                            ? 'border-brand-gold bg-brand-gold/5'
                            : 'border-brand-dark-blue bg-brand-dark-blue/5'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      {isAdvanced && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-white rounded-full px-4 py-1.5 text-xs font-bold shadow-lg">
                          <Trans>BEST VALUE</Trans>
                        </div>
                      )}
                      {isCurrentPlan && (
                        <div
                          className={`absolute -top-3 -right-3 ${
                            isCancelled ? 'bg-brand-gold' : 'bg-brand-green'
                          } text-white rounded-full p-2 shadow-lg`}
                        >
                          {isCancelled ? (
                            <XCircle className="h-5 w-5" />
                          ) : (
                            <Check className="h-5 w-5" />
                          )}
                        </div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-brand-dark-blue mb-3">{plan.name}</h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-brand-dark-blue">
                            ${price}
                          </span>
                          {price > 0 && (
                            <span className="text-sm font-medium text-brand-dark-blue/60">
                              /month
                            </span>
                          )}
                        </div>
                        {price > 0 && (
                          <p className="text-xs text-brand-dark-blue/50 mt-1">
                            {billingPeriod === 'annually'
                              ? 'billed annually'
                              : 'billed monthly'}
                          </p>
                        )}
                      </div>
                      
                      <ul className="space-y-3 mb-6 flex-grow">
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-brand-green mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-brand-dark-blue font-medium">
                            {key === 'advanced'
                              ? <Trans>Unlimited practices</Trans>
                              : getPracticePeriodText(key)}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-brand-green mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-brand-dark-blue font-medium">
                            {key === 'advanced' ? <Trans>Unlimited recording time</Trans> : <Trans>Up to 2 min per recording</Trans>}
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-brand-green mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-brand-dark-blue font-medium"><Trans>AI-powered feedback</Trans></span>
                        </li>
                        {price > 0 && (
                          <li className="flex items-start gap-3">
                            <Check className="h-5 w-5 text-brand-green mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-brand-dark-blue font-medium"><Trans>Priority support</Trans></span>
                          </li>
                        )}
                      </ul>
                      
                      {key !== 'free' && (
                        <Button
                          className={`w-full py-6 text-base font-bold rounded-xl transition-all duration-200 ${
                            isAdvanced
                              ? 'bg-brand-gold hover:bg-brand-gold/90 text-white shadow-lg'
                              : 'bg-gradient-to-r from-brand-dark-blue to-brand-green hover:from-brand-dark-blue/90 hover:to-brand-green/90 text-white'
                          }`}
                          variant="default"
                          disabled={
                            (isCurrentPlan && !isCancelled) || isLoading
                          }
                          onClick={() => handleSubscribe(key)}
                        >
                          {isPlanLoading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              <Trans>Processing...</Trans>
                            </>
                          ) : isCurrentPlan ? (
                            isCancelled ? (
                              <Trans>Reactivate</Trans>
                            ) : (
                              <Trans>Current Plan</Trans>
                            )
                          ) : currentPlan === 'advanced' && key === 'pro' ? (
                            <Trans>Downgrade</Trans>
                          ) : (
                            <Trans>Subscribe Now</Trans>
                          )}
                        </Button>
                      )}
                      {key === 'free' && (
                        <div className="w-full py-6 text-center text-brand-dark-blue/50 font-medium">
                          <Trans>Current Plan</Trans>
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        ) : view === 'loading' ? (
          <LoadingView />
        ) : view === 'payment' && clientSecret && selectedPlan ? (
          <div className="p-6 sm:p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-2xl font-bold text-brand-dark-blue"><Trans>Complete Your Subscription</Trans></DialogTitle>
              <DialogDescription className="text-brand-dark-blue/70">
                <Trans>Enter your payment details to subscribe to the</Trans> {PLANS[selectedPlan].name} <Trans>plan</Trans>
              </DialogDescription>
            </DialogHeader>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#091850', // brand-dark-blue
                    colorBackground: '#ffffff',
                    colorText: '#091850', // brand-dark-blue
                    colorDanger: '#D4B086', // brand-error
                    fontFamily:
                      'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '6px',
                  },
                },
              }}
            >
              <PaymentForm
                clientSecret={clientSecret}
                plan={selectedPlan}
                onSuccess={handlePaymentSuccess}
                onCancel={() => setView('plans')}
              />
            </Elements>
          </div>
        ) : (
          successPlan && (
            <SuccessView plan={successPlan} onClose={handleClose} />
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
